import axiosInstance from './axios';

export interface HeadcountRow {
  departmentId: number;
  departmentCode: string;
  departmentName: string;
  activeHeadcount: number;
  inactiveHeadcount: number;
  joinersInMonth: number;
  totalHeadcount: number;
}

export interface LeaveSummaryRow {
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  departmentId: number;
  departmentName: string;
  totalPaidDays: number;
  totalUnpaidDays: number;
  lateSubmissionCount: number;
  totalRequests: number;
  currentBalance: number;
}

export interface LateSummaryRow {
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  departmentId: number;
  departmentName: string;
  minutesApproved: number;
  quotaMinutes: number;
  usagePercentage: number;
  overQuotaCount: number;
}

export interface ApprovalSlaRow {
  managerId: number;
  managerName: string;
  managerEmail: string;
  departmentId: number;
  departmentName: string;
  pendingCount: number;
  overdueCount: number;
  avgDecisionHours: number;
  totalDecisions: number;
}

export interface HeadcountReportFilters {
  month?: string; // YYYY-MM
  departmentId?: number;
  includeSubtree?: boolean;
}

export interface LeaveSummaryReportFilters {
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  departmentId?: number;
  includeSubtree?: boolean;
}

export interface LateSummaryReportFilters {
  month?: string; // YYYY-MM
  departmentId?: number;
  includeSubtree?: boolean;
}

export interface ApprovalSlaReportFilters {
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  departmentId?: number;
  includeSubtree?: boolean;
}

class ReportsService {
  private basePath = '/reports';

  /**
   * Get headcount report (RPT-EMP-01)
   */
  async getHeadcountReport(
    filters?: HeadcountReportFilters
  ): Promise<HeadcountRow[]> {
    const response = await axiosInstance.get<HeadcountRow[]>(
      `${this.basePath}/headcount`,
      { params: filters }
    );
    return response.data;
  }

  /**
   * Get leave summary report (RPT-LEAVE-01)
   */
  async getLeaveSummaryReport(
    filters?: LeaveSummaryReportFilters
  ): Promise<LeaveSummaryRow[]> {
    const response = await axiosInstance.get<LeaveSummaryRow[]>(
      `${this.basePath}/leave-summary`,
      { params: filters }
    );
    return response.data;
  }

  /**
   * Get late/early summary report (RPT-LATE-01)
   */
  async getLateSummaryReport(
    filters?: LateSummaryReportFilters
  ): Promise<LateSummaryRow[]> {
    const response = await axiosInstance.get<LateSummaryRow[]>(
      `${this.basePath}/late-summary`,
      { params: filters }
    );
    return response.data;
  }

  /**
   * Get approval SLA report (RPT-APP-01)
   */
  async getApprovalSlaReport(
    filters?: ApprovalSlaReportFilters
  ): Promise<ApprovalSlaRow[]> {
    const response = await axiosInstance.get<ApprovalSlaRow[]>(
      `${this.basePath}/approval-sla`,
      { params: filters }
    );
    return response.data;
  }

  /**
   * Export headcount report to Excel
   */
  async exportHeadcountReport(filters?: HeadcountReportFilters): Promise<void> {
    const response = await axiosInstance.get(
      `${this.basePath}/headcount/export`,
      {
        params: filters,
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fileName = `headcount-report-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Export leave summary report to Excel
   */
  async exportLeaveSummaryReport(filters?: LeaveSummaryReportFilters): Promise<void> {
    const response = await axiosInstance.get(
      `${this.basePath}/leave-summary/export`,
      {
        params: filters,
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fileName = `leave-summary-report-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Export late summary report to Excel
   */
  async exportLateSummaryReport(filters?: LateSummaryReportFilters): Promise<void> {
    const response = await axiosInstance.get(
      `${this.basePath}/late-summary/export`,
      {
        params: filters,
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fileName = `late-summary-report-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Export approval SLA report to Excel
   */
  async exportApprovalSlaReport(filters?: ApprovalSlaReportFilters): Promise<void> {
    const response = await axiosInstance.get(
      `${this.basePath}/approval-sla/export`,
      {
        params: filters,
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const fileName = `approval-sla-report-${new Date().toISOString().split('T')[0]}.xlsx`;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const reportsService = new ReportsService();
